import { replaceHistory } from "@tm/insurance/utils";
import { theme } from "@tm/utility/global";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  FilterChipIcon,
  SortChipIcon,
} from "../../../CommonLandingMweb/shared/MwebIcons";
import {
  isNetPricingView,
  PRICING_VIEW_CHANGE_EVENT,
  PRICING_VIEW_NET,
  PRICING_VIEW_REGULAR,
  readNetPricingFromUrl,
} from "../../../mypartner/contexts/FilterContext";
import { FilterBottomSheet } from "./FilterBottomSheet";
import type {
  BottomSheetData,
  FilterSortCallbacks,
  FilterSortChip,
  NestedFilterSection,
  NestedFilterState,
} from "./FilterSort.types";
import { SortBottomSheet } from "./SortBottomSheet";

function intersectSets(sets: Set<string>[]): string[] {
  if (sets.length === 0) return [];
  if (sets.length === 1) return Array.from(sets[0]);
  const [first, ...rest] = sets;
  return Array.from(first).filter((id) => rest.every((s) => s.has(id)));
}

interface FilterSortBarProps {
  chips: FilterSortChip[];
  callbacks: FilterSortCallbacks;
}

export const FilterSortBar = ({ chips, callbacks }: FilterSortBarProps) => {
  const [openSheet, setOpenSheet] = useState<BottomSheetData | null>(null);
  const [activeSort, setActiveSort] = useState<string | null>(null);
  const [isNetPricing, setIsNetPricing] = useState(() =>
    readNetPricingFromUrl()
  );
  const [selectedPlanChips, setSelectedPlanChips] = useState<Set<string>>(
    () => new Set()
  );
  const [nestedFilterState, setNestedFilterState] = useState<NestedFilterState>(
    {}
  );
  const [filterCount, setFilterCount] = useState(0);

  const syncPricingViewInUrl = useCallback((isNet: boolean) => {
    replaceHistory(
      "pricingView",
      isNet ? PRICING_VIEW_NET : PRICING_VIEW_REGULAR
    );
  }, []);

  const syncNetPricingFromUrl = useCallback(() => {
    if (typeof window === "undefined") return;
    const urlPricingView = new URLSearchParams(window.location.search).get(
      "pricingView"
    );
    if (!urlPricingView) return;
    const isNet = isNetPricingView(urlPricingView);
    setIsNetPricing(isNet);
    callbacks.onPriceUpdate(isNet);
  }, [callbacks]);

  useEffect(() => {
    syncNetPricingFromUrl();
    window.addEventListener(PRICING_VIEW_CHANGE_EVENT, syncNetPricingFromUrl);
    return () =>
      window.removeEventListener(
        PRICING_VIEW_CHANGE_EVENT,
        syncNetPricingFromUrl
      );
  }, [syncNetPricingFromUrl]);

  const isChipActive = useCallback(
    (chip: FilterSortChip): boolean => {
      if (chip.action === "price_update") return isNetPricing;
      if (chip.action === "plan_select")
        return selectedPlanChips.has(chip.title);
      if (chip.action === "bottomSheet" && chip.bottomSheetData) {
        if (chip.bottomSheetData.action === "sort") return activeSort !== null;
        if (chip.bottomSheetData.action === "nested") return filterCount > 0;
      }
      return false;
    },
    [isNetPricing, selectedPlanChips, activeSort, filterCount]
  );

  const chipLabel = useCallback(
    (chip: FilterSortChip): string => {
      if (
        chip.action === "bottomSheet" &&
        chip.bottomSheetData?.action === "nested" &&
        filterCount > 0
      ) {
        return `${chip.title} (${filterCount})`;
      }
      return chip.title;
    },
    [filterCount]
  );

  const handleChipPress = useCallback(
    (chip: FilterSortChip) => {
      if (chip.action === "bottomSheet" && chip.bottomSheetData) {
        setOpenSheet(chip.bottomSheetData);
        return;
      }

      if (chip.action === "price_update") {
        const next = !isNetPricing;
        setIsNetPricing(next);
        syncPricingViewInUrl(next);
        callbacks.onPriceUpdate(next);
        return;
      }

      if (chip.action === "plan_select" && chip.quoteIds) {
        const deselecting = selectedPlanChips.has(chip.title);
        const next = new Set(selectedPlanChips);
        if (deselecting) {
          next.delete(chip.title);
        } else {
          next.add(chip.title);
        }
        setSelectedPlanChips(next);

        if (next.size === 0) {
          callbacks.onPlanSelect([]);
        } else {
          const sets: Set<string>[] = [];
          chips.forEach((c) => {
            if (c.action === "plan_select" && c.quoteIds && next.has(c.title)) {
              sets.push(new Set(c.quoteIds));
            }
          });
          callbacks.onPlanSelect(intersectSets(sets));
        }
      }
    },
    [isNetPricing, selectedPlanChips, chips, callbacks, syncPricingViewInUrl]
  );

  const handleSortApply = useCallback(
    (sortKey: string, quoteIds: string[]) => {
      setActiveSort(sortKey);
      callbacks.onSortApply(sortKey, quoteIds);
      setOpenSheet(null);
    },
    [callbacks]
  );

  const handleFilterApply = useCallback(
    (state: NestedFilterState, sections: NestedFilterSection[]) => {
      setNestedFilterState(state);
      let count = 0;
      const quoteIdSets: Set<string>[] = [];

      Object.entries(state).forEach(([_label, selectedKeys]) => {
        const section = sections.find((s) => s.label === _label);
        if (!section) return;

        const isDefault =
          section.data.defaultValue &&
          selectedKeys.size === 1 &&
          selectedKeys.has(section.data.defaultValue);

        if (!isDefault) count += selectedKeys.size;

        if (section.data.action === "plan_select" && selectedKeys.size > 0) {
          const sectionIds = new Set<string>();
          selectedKeys.forEach((key) => {
            const item = section.data.list.find((li) => li.key === key);
            if (item?.quoteIds)
              item.quoteIds.forEach((id) => sectionIds.add(id));
          });
          if (sectionIds.size > 0) quoteIdSets.push(sectionIds);
        }
      });
      setFilterCount(count);

      const priceSection = sections.find(
        (s) => s.data.action === "price_update"
      );
      if (priceSection) {
        const priceKeys = state[priceSection.label];
        if (priceKeys) {
          const isNet = priceKeys.has("net_price");
          setIsNetPricing(isNet);
          syncPricingViewInUrl(isNet);
          callbacks.onPriceUpdate(isNet);
        }
      }

      const planSections = sections.filter(
        (s) => s.data.action === "plan_select"
      );
      if (planSections.length > 0) {
        const intersectedIds = intersectSets(quoteIdSets);
        callbacks.onPlanSelect(intersectedIds);
      }

      setOpenSheet(null);
    },
    [callbacks, syncPricingViewInUrl]
  );

  const handleFilterReset = useCallback(() => {
    setNestedFilterState({});
    setFilterCount(0);
    setIsNetPricing(false);
    setSelectedPlanChips(new Set());
    setActiveSort(null);
    syncPricingViewInUrl(false);
    callbacks.onPriceUpdate(false);
    callbacks.onPlanSelect([]);
    setOpenSheet(null);
  }, [callbacks, syncPricingViewInUrl]);

  const activeFilterQuoteIds = useMemo((): Set<string> | null => {
    const sets: Set<string>[] = [];

    // From plan_select chips
    if (selectedPlanChips.size > 0) {
      chips.forEach((c) => {
        if (
          c.action === "plan_select" &&
          c.quoteIds &&
          selectedPlanChips.has(c.title)
        ) {
          sets.push(new Set(c.quoteIds));
        }
      });
    }

    // From nested filter state (plan_select sections)
    const nestedSheet = chips.find(
      (c) =>
        c.action === "bottomSheet" && c.bottomSheetData?.action === "nested"
    );
    if (nestedSheet?.bottomSheetData) {
      const sections = nestedSheet.bottomSheetData
        .list as NestedFilterSection[];
      Object.entries(nestedFilterState).forEach(([label, selectedKeys]) => {
        const section = sections.find((s) => s.label === label);
        if (!section || section.data.action !== "plan_select") return;
        if (selectedKeys.size === 0) return;
        const isDefault =
          section.data.defaultValue &&
          selectedKeys.size === 1 &&
          selectedKeys.has(section.data.defaultValue);
        if (isDefault) return;

        const sectionIds = new Set<string>();
        selectedKeys.forEach((key) => {
          const item = section.data.list.find((li) => li.key === key);
          if (item?.quoteIds) item.quoteIds.forEach((id) => sectionIds.add(id));
        });
        if (sectionIds.size > 0) sets.push(sectionIds);
      });
    }

    if (sets.length === 0) return null;
    return new Set(intersectSets(sets));
  }, [selectedPlanChips, chips, nestedFilterState]);

  const sortDefault = useMemo(() => {
    if (openSheet?.action === "sort")
      return activeSort || openSheet.defaultValue;
    return undefined;
  }, [openSheet, activeSort]);

  return (
    <>
      <BarWrap>
        <ScrollRail>
          {chips.map((chip, idx) => {
            const active = isChipActive(chip);
            const hasDropdown = chip.action === "bottomSheet";

            return (
              <ChipWrap key={`${chip.title}-${idx}`}>
                {chip.badgeUrl ? (
                  <BadgePos>
                    <BadgeImg src={chip.badgeUrl} alt="" />
                  </BadgePos>
                ) : null}
                <Chip
                  type="button"
                  $active={active}
                  onClick={() => handleChipPress(chip)}
                >
                  <ChipText $active={active}>{chipLabel(chip)}</ChipText>
                  {hasDropdown ? (
                    <IconWrap $active={active}>
                      {chip.bottomSheetData?.action === "nested" ? (
                        <FilterChipIcon />
                      ) : (
                        <SortChipIcon />
                      )}
                    </IconWrap>
                  ) : null}
                </Chip>
              </ChipWrap>
            );
          })}
        </ScrollRail>
        <FadeEdge aria-hidden />
      </BarWrap>

      {openSheet?.action === "sort" ? (
        <SortBottomSheet
          data={openSheet}
          defaultValue={sortDefault}
          activeFilterQuoteIds={activeFilterQuoteIds}
          onApply={handleSortApply}
          onClose={() => setOpenSheet(null)}
        />
      ) : null}

      {openSheet?.action === "nested" ? (
        <FilterBottomSheet
          data={openSheet}
          initialState={nestedFilterState}
          onApply={handleFilterApply}
          onReset={handleFilterReset}
          onClose={() => setOpenSheet(null)}
        />
      ) : null}
    </>
  );
};

const BarWrap = styled.div`
  position: relative;
  overflow: hidden;
`;

const ScrollRail = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding: 0 12px 0 16px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ChipWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const BadgePos = styled.div`
  position: absolute;
  top: -10px;
  left: -1px;
  padding-left: 8px;
  z-index: 1;
  pointer-events: none;
`;

const BadgeImg = styled.img`
  height: 14px;
  width: auto;
  object-fit: contain;
  display: block;
`;

const Chip = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 32px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$active ? theme.blueText : theme.lightSilver)};
  background: ${(p) => (p.$active ? theme.lightBlueBg : theme.whiteText)};
  cursor: pointer;
  white-space: nowrap;
  font: inherit;
`;

const ChipText = styled.span<{ $active: boolean }>`
  font-size: 12px;
  font-weight: ${(p) => (p.$active ? 700 : 400)};
  color: ${(p) => (p.$active ? theme.blueText : theme.greyText6)};
  line-height: 1;
`;

const IconWrap = styled.span<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${(p) => (p.$active ? theme.blueText : theme.greyText6)};
`;

const FadeEdge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 48px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    ${theme.whiteText} 82%
  );
  pointer-events: none;
`;
